import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import spotifyIcon from 'assets/spotify-icon.svg';
import { MediaListItem } from 'components/common/MediaListItem';
import { TrackCover } from 'components/track/TrackCover';
import { Track, TrackVendor } from 'models/Track';
import { TrackRequest, TrackRequestType } from 'models/TrackRequest';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestHash } from 'utils/requestHash';
interface SpotifySong {
  title: string;
  artist: string;
  imageUrl: string;
  trackUrl: string;
  duration_ms: number;
  index: number;
}

interface SpotifyPlaylistResponse {
  playlistId: string;
  playlistName: string;
  playlistImageUrl: string;
  songs: SpotifySong[];
}

export interface SpotifyImporterProps {
  onRequest: (request: TrackRequest<Track>) => void;
  onImportRequests?: (requests: TrackRequest<Track>[]) => Promise<void>;
  onRemoveRequest: (request: TrackRequest) => void;
  requests: TrackRequest[];
  canRequest: boolean;
}

export function SpotifyImporter(props: SpotifyImporterProps) {
  const { t } = useTranslation();
  const { onRequest, requests, canRequest } = props;
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlist, setPlaylist] = useState<SpotifyPlaylistResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const theme = useTheme();

  const requestedTrackKeys = useMemo(() => {
    const keys = new Set<string>();
    requests.forEach((req) => {
      if (req.type === TrackRequestType.Track && req.value) {
        const track = req.value as Track;
        keys.add(`${track.artist}-${track.title}`);
      }
    });
    return keys;
  }, [requests]);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlaylistUrl(event.target.value);
    setError(null);
  };

  const handleClearUrl = () => {
    setPlaylistUrl('');
    setError(null);
  };

  // const handlePasteClick = async () => {
  //   try {
  //     const clipboardText = await navigator.clipboard.readText();

  //     if (clipboardText) {
  //       setPlaylistUrl(clipboardText);
  //       setError(null);
  //       // Automatically fetch the playlist after pasting
  //       await fetchPlaylist(clipboardText);
  //     }
  //   } catch (err) {
  //     console.error('Failed to read clipboard:', err);
  //     setError('Failed to read clipboard. Please paste the URL manually.');
  //   }
  // };

  const handleFetchPlaylist = async () => {
    if (!playlistUrl.trim()) {
      // If no URL is entered, try to paste from clipboard
      // await handlePasteClick();
      return;
    }
    await fetchPlaylist(playlistUrl);
  };

  const fetchPlaylist = async (url: string) => {
    if (!url.trim()) {
      setError('Please enter a Spotify playlist URL');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://spotify.dj-play.it/playlist?url=${encodeURIComponent(url)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: SpotifyPlaylistResponse = await response.json();
      setPlaylist(data);

      // Pre-check all tracks
      const trackIds = new Set(data.songs.map((song) => `${song.artist}-${song.title}`));
      setSelectedTracks(trackIds);
    } catch (err) {
      console.error('Error fetching playlist:', err);
      setError('Failed to fetch playlist. Please check the URL and try again.');
      setPlaylist(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrackSelectionChange = (trackId: string, checked: boolean) => {
    const newSelected = new Set(selectedTracks);
    if (checked) {
      newSelected.add(trackId);
    } else {
      newSelected.delete(trackId);
    }
    setSelectedTracks(newSelected);
  };

  const handleSelectAll = () => {
    if (playlist) {
      const allTrackIds = new Set(playlist.songs.map((song) => `${song.artist}-${song.title}`));
      setSelectedTracks(allTrackIds);
    }
  };

  const handleDeselectAll = () => {
    setSelectedTracks(new Set());
  };

  const handleImportSelected = async () => {
    if (!playlist || isImporting) return;

    setIsImporting(true);
    try {
      const selectedSongs = playlist.songs.filter((song) =>
        selectedTracks.has(`${song.artist}-${song.title}`)
      );

      const trackRequestsToImport: TrackRequest<Track>[] = await Promise.all(
        selectedSongs.map(async (song) => {
          const hash = await requestHash(song.title, song.artist);

          // Create new track request
          const track: Track = {
            artist: song.artist,
            title: song.title,
            // TODO: Get Album name from Spotify API
            collection: '',
            artworkUrl: song.imageUrl,
            vendor: TrackVendor.Spotify,
            vendorUrl: song.trackUrl,
            duration: Math.ceil(song.duration_ms / 1000),
          };

          return {
            value: track,
            type: TrackRequestType.Track,
            hash: hash,
            index: song.index,
            playlistInfo: {
              name: playlist.playlistName,
              image: playlist.playlistImageUrl,
              id: playlist.playlistId,
            },
          };
        })
      );

      if (props.onImportRequests) {
        await props.onImportRequests(trackRequestsToImport);
      } else {
        // Fallback
        for (const req of trackRequestsToImport) {
          onRequest(req);
        }
      }

      // Clear selection after import
      setSelectedTracks(new Set());
      setPlaylistUrl('');
      setPlaylist(null);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error importing playlist tracks:', err);
    } finally {
      setIsImporting(false);
    }
  };

  const openSpotifyTrack = (trackUrl: string) => {
    window.open(trackUrl, '_blank');
  };

  useEffect(() => {
    if (playlistUrl.trim()) {
      fetchPlaylist(playlistUrl);
    }
  }, [playlistUrl]);

  return (
    <>
      <Backdrop
        open={isImporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          flexDirection: 'column',
          gap: 2,
          color: '#fff',
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1" fontWeight="bold">
          {t('savingSelectedTracks', 'Saving {{size}} tracks...', {
            size: selectedTracks.size,
          })}
        </Typography>
      </Backdrop>
      {/* <Box>
        <Typography variant="h6">{t('yourSpotifyPlaylist', 'Your Spotify playlist')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('copySpotifyPlaylistAndPaste', 'Copy your Spotify playlist link and paste it here')}
        </Typography>
      </Box> */}
      <Box>
        <TextField
          // sx={{
          //   flex: 1,
          //   '& fieldset': {
          //     borderTopRightRadius: 0,
          //     borderBottomRightRadius: 0,
          //   },
          // }}
          fullWidth
          label={
            <>
              <img
                src={spotifyIcon}
                style={{
                  width: '1em',
                  height: '1em',
                  verticalAlign: 'center',
                  marginRight: theme.spacing(1),
                }}
              />
              {t('yourSpotifyPlaylist', 'Your Spotify playlist')}
            </>
          }
          // placeholder="https://open.spotify.com/playlist/abc..."
          value={playlistUrl}
          onChange={handleUrlChange}
          onFocus={(e) => e.target.select()}
          disabled={isLoading || isImporting}
          error={!!error}
          color={playlist && playlistUrl ? 'success' : 'primary'}
          helperText={
            <FormHelperText error={!!error} sx={{ ml: 0 }}>
              {error
                ? error
                : playlist && playlistUrl
                ? t('playlistLoaded', 'Playlist loaded: {{name}} ({{size}} tracks)', {
                    name: playlist.playlistName,
                    size: playlist.songs.length,
                  })
                : t(
                    'spotifyPlaylistUrlHelper',
                    'Share your Spotify playlist and paste the link here'
                  )}
            </FormHelperText>
          }
          slotProps={{
            input: {
              endAdornment: isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <IconButton onClick={handleClearUrl} edge="end" disabled={isImporting}>
                  <ClearIcon />
                </IconButton>
              ),
            },
          }}
        />
      </Box>

      {playlist && (
        <Card sx={{ overflow: 'visible' }}>
          <CardContent>
            <MediaListItem
              size="medium"
              media={
                <TrackCover
                  title={playlist.playlistName}
                  cover={playlist.playlistImageUrl}
                  size="medium"
                />
              }
              title={playlist.playlistName}
              subtitle={t('lengthTracks', '{{length}} tracks', {
                length: playlist.songs.length,
                size: selectedTracks.size,
              })}
              trailing={
                <IconButton onClick={() => setIsExpanded((prev) => !prev)}>
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
            />
          </CardContent>

          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Stack useFlexGap direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ flex: '1 1 auto' }}
                  onClick={handleSelectAll}
                  disabled={isImporting}
                >
                  {t('selectAll', 'Select All')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ flex: '1 1 auto' }}
                  onClick={handleDeselectAll}
                  disabled={isImporting}
                >
                  {t('deselectAll', 'Deselect All')}
                </Button>
              </Stack>

              <Stack>
                {playlist.songs.map((song, index) => {
                  const trackId = `${song.artist}-${song.title}`;
                  const isSelected = selectedTracks.has(trackId);
                  const isAlreadyRequested = requestedTrackKeys.has(trackId);

                  return (
                    <MediaListItem
                      key={index}
                      size="small"
                      media={<TrackCover title={song.title} cover={song.imageUrl} size="small" />}
                      title={song.title}
                      onClick={() => handleTrackSelectionChange(trackId, !isSelected)}
                      pretitle={song.artist}
                      leading={
                        <Checkbox
                          checked={isSelected || isAlreadyRequested}
                          onChange={(e) => handleTrackSelectionChange(trackId, e.target.checked)}
                          disabled={isAlreadyRequested || !canRequest || isImporting}
                        />
                      }
                      trailing={
                        <IconButton onClick={() => openSpotifyTrack(song.trackUrl)}>
                          <PlayArrowIcon />
                        </IconButton>
                      }
                    />
                  );
                })}
              </Stack>
            </CardContent>

            <Box
              sx={{
                position: 'sticky',
                bottom: theme.spacing(2),
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                mb: 4,
                zIndex: theme.zIndex.speedDial,
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={handleImportSelected}
                disabled={selectedTracks.size === 0 || !canRequest || isImporting}
                endIcon={
                  isImporting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Chip
                      label={selectedTracks.size}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.15)',
                        color: 'inherit',
                        fontWeight: theme.typography.fontWeightBold,
                      }}
                    />
                  )
                }
                sx={{
                  boxShadow: `0 0 24px 6px ${alpha(theme.palette.success.main, 0.25)}`,
                  fontWeight: theme.typography.fontWeightBold,
                  textTransform: 'none',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    boxShadow: `0 0 24px 6px ${alpha(theme.palette.success.main, 0.25)}`,
                  },
                }}
              >
                {t('requestSelectedTracks', 'Request tracks', {
                  size: selectedTracks.size,
                })}
              </Button>
            </Box>
          </Collapse>
        </Card>
      )}
    </>
  );
}
